/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

/**
 * A price implies its transaction (plan §2): a short-term price marks
 * Temporada and an auction value marks Leilão. Never an error, and never the
 * reverse: a transaction the user marked is not removed for lack of a price.
 * The backend applies the same rule on write; the form mirrors it so the
 * chip lights up while the user types.
 */

import type { PropertyFormValues } from './schema';
import { AUCTION_TRANSACTION, SHORT_TERM_TRANSACTION } from './schema.constants';

type PriceFields = Pick<
  Partial<PropertyFormValues>,
  'short_term_price' | 'auction_first_price' | 'auction_second_price'
>;

/** Form fields whose value marks a transaction, and the transaction each marks. */
export const PRICE_TRANSACTION_FIELDS: ReadonlyArray<readonly [keyof PriceFields, string]> = [
  ['short_term_price', SHORT_TERM_TRANSACTION],
  ['auction_first_price', AUCTION_TRANSACTION],
  ['auction_second_price', AUCTION_TRANSACTION],
];

/**
 * Whether a price counts as filled (a positive number).
 *
 * @param value - The value held by the form.
 * @returns True when the price is greater than zero.
 */
function isPriced(value: unknown): boolean {
  return typeof value === 'number' && value > 0;
}

/**
 * Adds the transactions the prices imply.
 *
 * @param types - Transactions currently selected.
 * @param prices - The short-term and auction prices.
 * @returns A copy of the selection with the implied transactions appended.
 */
export function withPricedTransactions(types: readonly string[] | undefined, prices: PriceFields): string[] {
  const current = [...(types ?? [])];
  const missing = PRICE_TRANSACTION_FIELDS
    .filter(([field, transaction]) => isPriced(prices[field]) && !current.includes(transaction))
    .map(([, transaction]) => transaction);
  if (missing.length === 0) return current;
  return [...current, ...new Set(missing)];
}

/**
 * Whether a form field change can mark a transaction.
 *
 * @param name - The changed field.
 * @returns True for the short-term and auction price fields.
 */
export function isTransactionPriceField(name: string | undefined): boolean {
  return PRICE_TRANSACTION_FIELDS.some(([field]) => field === name);
}
